// src/App.tsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { io, type Socket } from "socket.io-client";
import styled from "@emotion/styled";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Toolbar,
  Typography,
  Chip,
  Stack,
  useTheme,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Fade,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  Divider,
} from "@mui/material";
import {
  PlayArrow,
  Stop,
  VideocamOff,
  Circle,
  Speed,
  AspectRatio,
  Timer,
  NetworkCheck,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#3498db",
    },
    secondary: {
      main: "#2ecc71",
    },
    error: {
      main: "#e74c3c",
    },
    background: {
      default: "#0a0a0a",
      paper: "#1a1a1a",
    },
  },
});

// Styled components
const StyledAppBar = styled(AppBar)`
  background: linear-gradient(45deg, #2c3e50 0%, #3498db 100%);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.3);
`;

const ConnectionChip = styled(Chip)<{ connected: boolean }>`
  ${({ connected, theme }) => `
    background-color: ${
      connected ? theme.palette.success.main : theme.palette.error.main
    };
    color: white;
    font-weight: bold;
    animation: ${connected ? "pulse 2s infinite" : "none"};
    
    @keyframes pulse {
      0% {
        box-shadow: 0 0 0 0 ${
          connected ? theme.palette.success.main : theme.palette.error.main
        }40;
      }
      70% {
        box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
      }
    }
  `}
`;

const VideoContainer = styled(Paper)<{ fullscreen: boolean }>`
  position: ${(props) => (props.fullscreen ? "fixed" : "relative")};
  top: ${(props) => (props.fullscreen ? "0" : "auto")};
  left: ${(props) => (props.fullscreen ? "0" : "auto")};
  right: ${(props) => (props.fullscreen ? "0" : "auto")};
  bottom: ${(props) => (props.fullscreen ? "0" : "auto")};
  z-index: ${(props) => (props.fullscreen ? "9999" : "auto")};
  min-height: ${(props) => (props.fullscreen ? "100vh" : "500px")};
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border: ${(props) => (props.fullscreen ? "none" : "2px solid #333")};
  border-radius: ${(props) => (props.fullscreen ? "0" : "12px")};
  overflow: hidden;
  position: relative;
`;

const VideoCanvas = styled("canvas")`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
  image-rendering: crisp-edges;
`;

const ControlButton = styled(Button)`
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
`;

const StatsCard = styled(Card)`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const FPSIndicator = styled(Box)<{ fps: number }>`
  padding: 4px 12px;
  border-radius: 16px;
  font-weight: bold;
  background-color: ${({ fps }) =>
    fps >= 25 ? "#2ecc71" : fps >= 15 ? "#f39c12" : "#e74c3c"};
  color: white;
  font-size: 0.875rem;
`;

// Type definitions
interface FrameMetadata {
  timestamp: number;
  frameNumber: number;
  originalSize: {
    width: number;
    height: number;
  };
}

interface StreamStats {
  fps: number;
  frameCount: number;
  latency: number;
  resolution: string;
  bandwidth: number;
  lastFrameTime: number;
  droppedFrames: number;
  recoveryEvents: number;
}

interface ServerToClientEvents {
  frame: (frameData: ArrayBuffer, metadata: FrameMetadata) => void;
  error: (message: string) => void;
}

interface ClientToServerEvents {
  "start-stream": (roomId: string) => void;
  "stop-stream": () => void;
}

// Configure socxket connection
const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL || "https://ios3.mobileboost.io";

// Constants for frame recovery
const MAX_FRAME_AGE_MS = 2000; // Maximum age of a frame before it's considered stale
const RECOVERY_THRESHOLD = 3; // Number of consecutive stale frames before triggering recovery

function SocketIOPOC() {
  const theme = useTheme();
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const [isRecovering, setIsRecovering] = useState(false);

  const [streamStats, setStreamStats] = useState<StreamStats>({
    fps: 0,
    frameCount: 0,
    latency: 0,
    resolution: "N/A",
    bandwidth: 0,
    lastFrameTime: 0,
    droppedFrames: 0,
    recoveryEvents: 0,
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const socketRef = useRef<Socket<
    ServerToClientEvents,
    ClientToServerEvents
  > | null>(null);
  const statsIntervalRef = useRef<NodeJS.Timer | null>(null);
  const frameTimesRef = useRef<number[]>([]);
  const bytesReceivedRef = useRef<number>(0);
  const bandwidthStartTimeRef = useRef<number>(Date.now());
  const consecutiveStaleFramesRef = useRef<number>(0);
  const lastDisplayedFrameRef = useRef<number>(0);

  // Calculate FPS from frame times
  const calculateFPS = useCallback(() => {
    const now = Date.now();
    const recentFrames = frameTimesRef.current.filter(
      (time) => now - time < 1000
    );
    frameTimesRef.current = recentFrames;
    return recentFrames.length;
  }, []);

  // Calculate bandwidth
  const calculateBandwidth = useCallback(() => {
    const now = Date.now();
    const duration = (now - bandwidthStartTimeRef.current) / 1000; // seconds
    const bandwidth = bytesReceivedRef.current / duration; // bytes per second

    // Reset counters every 5 seconds
    if (duration > 5) {
      bytesReceivedRef.current = 0;
      bandwidthStartTimeRef.current = now;
    }

    return bandwidth;
  }, []);

  // Recovery mechanism to clear frame buffer
  const triggerRecovery = useCallback(() => {
    console.log("Triggering recovery mechanism - clearing stale frames");
    setIsRecovering(true);

    // Clear any pending frames
    consecutiveStaleFramesRef.current = 0;

    // Update recovery stats
    setStreamStats((prev) => ({
      ...prev,
      recoveryEvents: prev.recoveryEvents + 1,
    }));

    // Clear recovery state after a short delay
    setTimeout(() => {
      setIsRecovering(false);
    }, 100);
  }, []);

  useEffect(() => {
    // Initialize socket connection
    const socket: Socket<ServerToClientEvents, ClientToServerEvents> = io(
      SOCKET_URL,
      {
        transports: ["websocket"],
      }
    );

    socketRef.current = socket;

    // Connection event handlers
    socket.on("connect", () => {
      console.log("Connected to streaming server");
      setIsConnected(true);
      setIsLoading(false);
      setError(null);
    });

    socket.on("disconnect", () => {
      console.log("Disconnected from streaming server");
      setIsConnected(false);
      setIsStreaming(false);
    });

    // Frame event handler with recovery mechanism
    socket.on(
      "frame",
      async (frameData: ArrayBuffer, metadata: FrameMetadata) => {
        if (!canvasRef.current || isRecovering) return;

        const receiveTime = Date.now();
        const frameAge = receiveTime - metadata.timestamp;

        // Check if frame is too old
        if (frameAge > MAX_FRAME_AGE_MS) {
          console.log(
            `Dropping stale frame: ${frameAge}ms old (frame #${metadata.frameNumber})`
          );

          // Update dropped frame counter
          setStreamStats((prev) => ({
            ...prev,
            droppedFrames: prev.droppedFrames + 1,
          }));

          // Track consecutive stale frames
          consecutiveStaleFramesRef.current++;

          // Trigger recovery if too many consecutive stale frames
          if (consecutiveStaleFramesRef.current >= RECOVERY_THRESHOLD) {
            triggerRecovery();
          }

          return; // Don't display this frame
        }

        // Reset consecutive stale frames counter on good frame
        consecutiveStaleFramesRef.current = 0;

        // Only process frames that are newer than the last displayed frame
        if (metadata.frameNumber <= lastDisplayedFrameRef.current) {
          console.log(`Dropping out-of-order frame #${metadata.frameNumber}`);
          setStreamStats((prev) => ({
            ...prev,
            droppedFrames: prev.droppedFrames + 1,
          }));
          return;
        }

        try {
          // Track frame time for FPS calculation
          frameTimesRef.current.push(receiveTime);
          bytesReceivedRef.current += frameData.byteLength;

          // Create blob and render to canvas
          const blob = new Blob([frameData], { type: "image/jpeg" });
          const imageBitmap = await createImageBitmap(blob);

          const canvas = canvasRef.current;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;

          // Set canvas size to match image
          if (
            canvas.width !== imageBitmap.width ||
            canvas.height !== imageBitmap.height
          ) {
            canvas.width = imageBitmap.width;
            canvas.height = imageBitmap.height;
          }

          // Draw frame
          ctx.drawImage(imageBitmap, 0, 0);

          // Update last displayed frame
          lastDisplayedFrameRef.current = metadata.frameNumber;

          // Update stats
          setStreamStats((prev) => ({
            ...prev,
            frameCount: metadata.frameNumber,
            resolution: `${metadata.originalSize.width}x${metadata.originalSize.height}`,
            lastFrameTime: metadata.timestamp,
            latency: frameAge,
          }));
        } catch (err) {
          console.error("Error rendering frame:", err);
        }
      }
    );

    // Error handler
    socket.on("error", (message: string) => {
      setError(message);
      console.error("Stream error:", message);
    });

    // Stats update interval
    statsIntervalRef.current = setInterval(() => {
      setStreamStats((prev) => ({
        ...prev,
        fps: calculateFPS(),
        bandwidth: calculateBandwidth(),
      }));
    }, 1000);

    // Cleanup on unmount
    return () => {
      socket.disconnect();
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
      }
    };
  }, [calculateFPS, calculateBandwidth, triggerRecovery, isRecovering]);

  const startStream = () => {
    if (socketRef.current) {
      const roomId = `6e80fef9-b836-4afc-92c9-d5c0882ea9d4`; // Shared room ID for all clients
      socketRef.current.emit("start-stream", roomId);
      setIsStreaming(true);
      setError(null);

      // Reset stats and recovery state
      frameTimesRef.current = [];
      bytesReceivedRef.current = 0;
      bandwidthStartTimeRef.current = Date.now();
      consecutiveStaleFramesRef.current = 0;
      lastDisplayedFrameRef.current = 0;
      setIsRecovering(false);
      setStreamStats((prev) => ({
        ...prev,
        droppedFrames: 0,
        recoveryEvents: 0,
      }));
    }
  };

  const stopStream = () => {
    if (socketRef.current) {
      socketRef.current.emit("stop-stream");
      setIsStreaming(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const formatBandwidth = (bytesPerSecond: number): string => {
    if (bytesPerSecond < 1024) return `${bytesPerSecond.toFixed(0)} B/s`;
    if (bytesPerSecond < 1048576)
      return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
    return `${(bytesPerSecond / 1048576).toFixed(2)} MB/s`;
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{ flexGrow: 1, minHeight: "100vh", bgcolor: "background.default" }}
      >
        {!isFullscreen && (
          <StyledAppBar position="static">
            <Toolbar>
              <Typography
                variant="h5"
                component="div"
                sx={{ flexGrow: 1, fontWeight: "bold" }}
              >
                iOS Simulator Stream
              </Typography>
              <ConnectionChip
                icon={<Circle sx={{ fontSize: 12 }} />}
                label={isConnected ? "Connected" : "Disconnected"}
                connected={isConnected}
                size="small"
              />
            </Toolbar>
          </StyledAppBar>
        )}

        {isLoading && <LinearProgress />}

        <Container
          maxWidth={isFullscreen ? false : "lg"}
          sx={{ mt: isFullscreen ? 0 : 4, mb: 4 }}
        >
          {error && !isFullscreen && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => {
                setError(null);
              }}
            >
              {error}
            </Alert>
          )}

          {isRecovering && !isFullscreen && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Recovering from network lag - clearing stale frames...
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Controls Section */}
            {!isFullscreen && (
              <Grid item xs={12}>
                <StatsCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                      Stream Controls
                    </Typography>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems="center"
                    >
                      <ControlButton
                        variant="contained"
                        color={isStreaming ? "error" : "primary"}
                        startIcon={isStreaming ? <Stop /> : <PlayArrow />}
                        onClick={isStreaming ? stopStream : startStream}
                        disabled={!isConnected}
                        size="large"
                      >
                        {isStreaming ? "Stop Stream" : "Start Stream"}
                      </ControlButton>

                      <Box sx={{ flexGrow: 1 }} />

                      {isStreaming && (
                        <>
                          <FPSIndicator fps={streamStats.fps}>
                            {streamStats.fps} FPS
                          </FPSIndicator>

                          <Chip
                            icon={<NetworkCheck />}
                            label={`${streamStats.latency}ms`}
                            color={
                              streamStats.latency < 100
                                ? "success"
                                : streamStats.latency < 300
                                ? "warning"
                                : "error"
                            }
                            size="small"
                          />

                          {streamStats.droppedFrames > 0 && (
                            <Chip
                              label={`${streamStats.droppedFrames} dropped`}
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </StatsCard>
              </Grid>
            )}

            {/* Video Display Section */}
            <Grid item xs={12}>
              <Fade in={true} timeout={800}>
                <VideoContainer elevation={3} fullscreen={isFullscreen}>
                  {isStreaming ? (
                    <>
                      <VideoCanvas ref={canvasRef} />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Tooltip
                          title={
                            isFullscreen ? "Exit fullscreen" : "Fullscreen"
                          }
                        >
                          <IconButton
                            onClick={toggleFullscreen}
                            sx={{
                              bgcolor: "rgba(0,0,0,0.5)",
                              color: "white",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                            }}
                          >
                            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {isFullscreen && (
                        <Box
                          sx={{ position: "absolute", bottom: 16, left: 16 }}
                        >
                          <ControlButton
                            variant="contained"
                            color="error"
                            startIcon={<Stop />}
                            onClick={stopStream}
                            size="small"
                          >
                            Stop Stream
                          </ControlButton>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box textAlign="center">
                      <VideocamOff
                        sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
                      />
                      <Typography variant="h6" color="text.secondary">
                        {isConnected
                          ? 'Click "Start Stream" to begin'
                          : "Waiting for connection..."}
                      </Typography>
                    </Box>
                  )}
                </VideoContainer>
              </Fade>
            </Grid>

            {/* Stats Section */}
            {!isFullscreen && isStreaming && (
              <Grid item xs={12}>
                <StatsCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Stream Statistics
                    </Typography>

                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={6} sm={3}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Speed sx={{ color: theme.palette.primary.main }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Frame Rate
                            </Typography>
                            <Typography variant="h6">
                              {streamStats.fps} FPS
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={6} sm={3}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AspectRatio
                            sx={{ color: theme.palette.secondary.main }}
                          />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Resolution
                            </Typography>
                            <Typography variant="h6">
                              {streamStats.resolution}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={6} sm={3}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Timer sx={{ color: theme.palette.warning.main }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Latency
                            </Typography>
                            <Typography variant="h6">
                              {streamStats.latency}ms
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={6} sm={3}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <NetworkCheck
                            sx={{ color: theme.palette.info.main }}
                          />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Bandwidth
                            </Typography>
                            <Typography variant="h6">
                              {formatBandwidth(streamStats.bandwidth)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total Frames:{" "}
                          {streamStats.frameCount.toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Dropped Frames:{" "}
                          {streamStats.droppedFrames.toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Recovery Events: {streamStats.recoveryEvents}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StatsCard>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default SocketIOPOC;
